// MyTemplates.jsx
// Uses the same cc-tpl-card grid design as CreateCertificate.jsx TemplatePicker.
// Cards show: badge, name, type, Cloud tag, × delete button, 👁 preview button.

import React, { useState, useMemo } from 'react';
import { renderCertificateForStudent } from './DynamicCertificateBuilder';
import TemplatePreview from './TemplatePreview';

// ── Type colour / label map ───────────────────────────────────────────────

const TYPE_COLORS = {
  TC:          '#1a237e',
  BONAFIDE:    '#B8860B',
  STUDY:       '#7c3aed',
  RECORDSHEET: '#374151',
  IDCARD:      '#c2185b',
};

const TYPE_LABELS = {
  TC:          'Transfer Certificate',
  BONAFIDE:    'Bonafide Certificate',
  STUDY:       'Study Certificate',
  RECORDSHEET: 'Record Sheet',
  IDCARD:      'Student ID Card',
};

// ── Normalise backend template ────────────────────────────────────────────

const toFilterKey = (raw) => {
  const t = String(raw || '').toLowerCase().replace(/[\s_-]/g, '');
  if (t === 'idcard' || t.includes('idcard')) return 'IDCARD';
  if (t === 'bonafide') return 'BONAFIDE';
  if (t === 'study') return 'STUDY';
  if (t === 'recordsheet' || t === 'record') return 'RECORDSHEET';
  if (t === 'tc' || t === 'transfer' || t.includes('transfer')) return 'TC';
  return String(raw || '').toUpperCase();
};

const normalise = (t) => {
  let certTypeFromJson = '';
  try { certTypeFromJson = JSON.parse(t.template)?.certType || ''; } catch {}
  return {
    id:            t.id,
    _backendId:    t.id,
    template_name: t.template_name,
    template_type: toFilterKey(certTypeFromJson || 'TC'),
    template_json: t.template,
    branch_id:     t.branch_id || null,
    created_at:    t.created_at || null,
  };
};

// ── Get preview HTML from a template ─────────────────────────────────────

// Render with empty data — unmatched {{variable}} tokens are converted
// by replaceVariables into blank underline spans, so all fields display
// as clean empty lines with no placeholder text.
const getPreviewHtml = (t) => {
  try {
    const cfg = JSON.parse(t.template_json);
    return renderCertificateForStudent(cfg, {}, [], {});
  } catch {
    return '<p style="padding:40px;text-align:center;color:#9ca3af;font-size:14px;">Preview not available</p>';
  }
};

// ── Main component ────────────────────────────────────────────────────────

const MyTemplates = ({ savedTemplates: backendTemplates = [], onDeleteTemplate, onEditTemplate }) => {
  const [filter,      setFilter]      = useState('ALL');
  const [confirmId,   setConfirmId]   = useState(null);
  const [previewTpl,  setPreviewTpl]  = useState(null);

  const types = ['ALL', 'TC', 'BONAFIDE', 'STUDY', 'RECORDSHEET', 'IDCARD'];

  // Normalise backend templates
  const templates = useMemo(() => backendTemplates.map(normalise), [backendTemplates]);

  // Filtered list
  const filtered = filter === 'ALL'
    ? templates
    : templates.filter(t => (t.template_type || '').toUpperCase() === filter);

  // Confirm delete
  const handleConfirmDelete = () => {
    const tpl = templates.find(t => t.id === confirmId);
    if (tpl && onDeleteTemplate) {
      onDeleteTemplate(tpl._backendId).catch(err => console.error('[MyTemplates delete]', err));
    }
    setConfirmId(null);
  };

  return (
    <div className="cc-picker" style={{ paddingTop: 0 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg,#6366f1,#7c3aed)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 16, flexShrink: 0,
          }}>
            <i className="fal fa-layer-group" />
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#1f2937', lineHeight: 1.2 }}>
              My Templates
              <span style={{
                marginLeft: 8, background: '#ede9fe', color: '#6366f1',
                borderRadius: 20, padding: '1px 10px', fontSize: 11, fontWeight: 700,
              }}>
                {templates.length}
              </span>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              Saved from Template Builder
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter chips — same class as CreateCertificate.jsx ── */}
      <div className="cc-picker-filters">
        {types.map(f => (
          <button
            key={f}
            className={`cc-filter-chip${filter === f ? ' active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'ALL' ? 'All Types' : f}
          </button>
        ))}
      </div>

      {/* ── Card grid — same class as CreateCertificate.jsx ── */}
      {filtered.length === 0 ? (
        <div className="cc-picker-empty" style={{ padding: '60px 32px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🗂</div>
          <div style={{ fontWeight: 800, fontSize: 15, color: '#374151', marginBottom: 6 }}>
            {filter === 'ALL' ? 'No saved templates yet' : `No ${filter} templates saved`}
          </div>
          <div style={{ fontSize: 13 }}>
            Go to <strong style={{ color: '#7c3aed' }}>Template Builder</strong> to design and save certificate templates.
          </div>
        </div>
      ) : (
        <div className="cc-picker-grid">
          {filtered.map(t => {
            const typeKey = (t.template_type || 'TC').toUpperCase();
            const color   = TYPE_COLORS[typeKey] || '#7c3aed';

            return (
              <div
                key={t.id}
                className="cc-tpl-card"
                style={{ '--card-color': color, cursor: 'default' }}
              >
                {/* × Delete — top-left */}
                <button
                  className="cc-tpl-delete-btn"
                  onClick={e => { e.stopPropagation(); setConfirmId(t.id); }}
                  title="Delete template"
                >
                  ×
                </button>

                {/* 👁 Preview — top-right */}
                <button
                  className="cc-tpl-preview-btn"
                  onClick={e => { e.stopPropagation(); setPreviewTpl(t); }}
                  title="View template preview"
                >
                  <i className="fal fa-eye" />
                </button>

                {/* Coloured circle badge */}
                <div className="cc-tpl-badge" style={{ background: color }}>
                  {typeKey.slice(0, 2)}
                </div>

                {/* Name */}
                <div className="cc-tpl-name">{t.template_name}</div>

                {/* Type label */}
                <div className="cc-tpl-type">
                  {TYPE_LABELS[typeKey] || typeKey}
                </div>

                {/* Cloud tag */}
                <div className="cc-tpl-tag cc-tpl-tag--builtin" style={{ background: '#eff6ff', color: '#2563eb' }}>
                  ☁ Cloud
                </div>

                {/* ✏ Edit button */}
                {onEditTemplate && (
                  <button
                    className="cc-tpl-edit-btn"
                    onClick={e => { e.stopPropagation(); onEditTemplate(t); }}
                    title="Edit template in builder"
                  >
                    <i className="fal fa-pencil" style={{ marginRight: 4 }} />
                    Edit
                  </button>
                )}

              </div>
            );
          })}
        </div>
      )}

      {/* ── Delete confirm dialog — same class as CreateCertificate.jsx ── */}
      {confirmId && (
        <div className="cc-confirm-overlay" onClick={() => setConfirmId(null)}>
          <div className="cc-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="cc-confirm-icon">🗑️</div>
            <div className="cc-confirm-title">Delete Template?</div>
            <div className="cc-confirm-msg">
              This template will be permanently removed from your saved templates.
            </div>
            <div className="cc-confirm-actions">
              <button className="cc-confirm-cancel" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="cc-confirm-delete" onClick={handleConfirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Full-screen preview modal ── */}
      {previewTpl && (
        <TemplatePreview
          html={getPreviewHtml(previewTpl)}
          title={previewTpl.template_name}
          fileName={(previewTpl.template_name || 'template').replace(/\s+/g, '_')}
          onClose={() => setPreviewTpl(null)}
        />
      )}

    </div>
  );
};

export default MyTemplates;
